/**
 * PeerTable (chair DESIGN §5.3): measures down, You | Peer median | Rank across, and with `bankNames` one more column per other bank. Used by "Your bank at the start", its all-banks drawer and the Bank page's Peers tab.
 * The measure names carry their definition in the glossary popover, on every row alike (no inline lines on some and not others).
 * Rows every bank prints the same fold into one line ("Measures where all banks are level") when other rows say something; a table of nothing but level rows prints them all.
 */
import { GxDataTable, type TableRow } from "../financials/DataTable";
import type { GxPhoneMode } from "./GxTable";

export interface PeerRow {
  readonly id: string;
  readonly label: string;
  /** The glossary term the label explains. */
  readonly term?: string;
  readonly you: string;
  readonly median: string | null;
  /** "3rd of 6", "level"; null where a rank has no meaning (a rate); "not ranked" for a measure with no better side. */
  readonly rank: string | null;
  /** One figure per other bank, in `bankNames` order. */
  readonly banks: readonly string[];
  /** The group the row sits under (the table prints a header row where it changes, when more than one group is shown). */
  readonly group?: string;
  /** Every bank prints the same figure. */
  readonly level: boolean;
}

export const LEVEL_GROUP = "Measures where all banks are level";

export function PeerTable({ rows, caption, testId, firstColumn = "Measure", bankNames, showPeers = true, foldLevel = true, labelledBy, reserveRank = false, medianLabel = "Peer median", rankLabel = "Rank", phone }: {
  readonly rows: readonly PeerRow[]; readonly caption: string; readonly testId: string; readonly firstColumn?: string;
  /** Names of the other banks: adds one column each (the expanded view). */
  readonly bankNames?: readonly string[] | undefined;
  /** False: the You column alone (the position every bank starts from has no gap to show). */
  readonly showPeers?: boolean;
  /** Fold the rows every bank prints the same into one line (default); false lists every row (the start position, which must show each of its measures). */
  readonly foldLevel?: boolean;
  /** Keep the Rank column when no row has a rank, so a table printed under another keeps the same column grid (the mix tables under the measures table). */
  readonly reserveRank?: boolean;
  /** The id of a visible heading that already names the table (no second, hidden caption). */
  readonly labelledBy?: string | undefined;
  readonly medianLabel?: string; readonly rankLabel?: string;
  /** The phone mode; default by column count. A peer matrix is a grid: it scrolls inside its box with the measure column pinned. */
  readonly phone?: GxPhoneMode;
}) {
  const hasRank = showPeers && (reserveRank || rows.some((r) => r.rank !== null));
  const columns = ["You", ...(showPeers ? [medianLabel] : []), ...(hasRank ? [rankLabel] : []), ...(bankNames ?? [])];
  const cells = (r: PeerRow): (string | null)[] => [r.you, ...(showPeers ? [r.median] : []), ...(hasRank ? [r.rank] : []), ...(bankNames === undefined ? [] : r.banks)];
  const folded = foldLevel && showPeers && rows.some((r) => !r.level) ? rows.filter((r) => r.level) : [];
  const shown = rows.filter((r) => !folded.includes(r));
  const toRow = (r: PeerRow): TableRow => ({ id: r.id, label: r.label, ...(r.term === undefined ? {} : { term: r.term }), cells: cells(r) });
  const groupHeads = new Set(shown.map((r) => r.group).filter((g): g is string => g !== undefined));
  let lastGroup: string | undefined;
  const body = shown.flatMap((r): TableRow[] => {
    const head = groupHeads.size > 1 && r.group !== undefined && r.group !== lastGroup ? [{ id: `group.${r.group}`, label: r.group, group: true, cells: columns.map(() => "") } satisfies TableRow] : [];
    lastGroup = r.group;
    return [...head, toRow(r)];
  });
  const tableRows: TableRow[] = [
    ...body,
    ...(folded.length === 0 ? [] : [{ id: "level", label: `${LEVEL_GROUP} (${folded.length})`, group: true, cells: columns.map(() => ""), children: folded.map((r) => toRow(r)) } satisfies TableRow]),
  ];
  return <div className="gx-peer-grid"><GxDataTable testId={testId} caption={caption} labelledBy={labelledBy} firstColumn={firstColumn} columns={columns} rows={tableRows} highlightColumn={columns.length > 1 ? 0 : undefined} {...(phone === undefined ? {} : { phone })} /></div>;
}
